import { BaseService } from "../../core/baseService";
import {
  RoomServiceInterface,
  GetRoomsParams,
  GetRoomByIdParams,
  CreateRoomParams,
  CreateRoomSeriesParams,
  UpdateRoomParams,
  DeleteRoomParams,
  GetRoomActivitySummaryParams,
  GetRoomDetailedActivityParams,
  GetRoomHistoryParams,
  GetRoomStatsParams,
  GetStayHistoryParams,
  RoomActivitySummary,
  RoomDetailedActivity,
  RoomHistory,
  RoomStats,
  StayHistoryItem,
  RoomDowngradeProgressDto,
  DesignateRoomsRequest,
  UndesignateRoomsRequest,
  DesignationResultDto,
  CanArchiveRoomResponse,
} from "../types/room.types";
import { RoomDto } from "../../../models";
import { PaginatedResponse } from "../../../models";

export class RoomService extends BaseService implements RoomServiceInterface {
  async getRooms(params: GetRoomsParams) {
    const queryParams = this.buildQueryParams({
      pageNumber: params.page,
      pageSize: params.pageSize,
      floor: params.floor,
      roomNumber: params.roomNumber,
    });

    return this.handleRequest<PaginatedResponse<RoomDto>>(
      this.client.get("/api/admin/rooms", { params: queryParams }),
    );
  }

  async getRoomsForDropdown() {
    return this.handleRequest<RoomDto[]>(
      this.client.get("/api/admin/rooms/dropdown"),
    );
  }

  async getRoomById(params: GetRoomByIdParams) {
    return this.handleRequest<RoomDto>(
      this.client.get(`/api/admin/rooms/${params.roomId}`),
    );
  }

  async createRoom(params: CreateRoomParams) {
    return this.handleRequest<RoomDto>(
      this.client.post("/api/admin/rooms", params),
    );
  }

  async createRoomSeries(params: CreateRoomSeriesParams) {
    return this.handleRequest<RoomDto[]>(
      this.client.post("/api/admin/rooms/series", params),
    );
  }

  async updateRoom(params: UpdateRoomParams) {
    const { roomId, data } = params;

    return this.handleRequest<RoomDto>(
      this.client.put(`/api/admin/rooms/${roomId}`, data),
    );
  }

  async deleteRoom(params: DeleteRoomParams) {
    return this.handleRequest<void>(
      this.client.delete(`/api/admin/rooms/${params.roomId}`),
    );
  }

  async archiveRoom(roomId: string) {
    return this.handleRequest<{ message: string }>(
      this.client.post(`/api/admin/rooms/${roomId}/archive`, {}),
    );
  }

  async unarchiveRoom(roomId: string) {
    return this.handleRequest<{ message: string }>(
      this.client.post(`/api/admin/rooms/${roomId}/unarchive`, {}),
    );
  }

  async getRoomActivitySummary(params: GetRoomActivitySummaryParams) {
    return this.handleRequest<RoomActivitySummary>(
      this.client.get(`/api/admin/rooms/${params.roomId}/activity`),
    );
  }

  async getRoomDetailedActivity(params: GetRoomDetailedActivityParams) {
    const queryParams = this.buildQueryParams({
      period: params.period || "current",
    });

    return this.handleRequest<RoomDetailedActivity>(
      this.client.get(`/api/admin/rooms/${params.roomId}/activity/detailed`, {
        params: queryParams,
      }),
    );
  }

  async getRoomHistory(params: GetRoomHistoryParams) {
    return this.handleRequest<RoomHistory[]>(
      this.client.get(`/api/admin/rooms/${params.roomId}/history`),
    );
  }

  async getRoomStayHistory(params: GetStayHistoryParams) {
    const queryParams = this.buildQueryParams({
      page: params.page,
      pageSize: params.pageSize,
    });

    return this.handleRequest<PaginatedResponse<StayHistoryItem>>(
      this.client.get(`/api/admin/rooms/${params.roomId}/stay-history`, {
        params: queryParams,
      }),
    );
  }

  async getRoomStats(params: GetRoomStatsParams) {
    const queryParams = this.buildQueryParams({
      floor: params.floor,
      roomNumber: params.roomNumber,
    });

    return this.handleRequest<RoomStats>(
      this.client.get("/api/admin/rooms/stats", { params: queryParams }),
    );
  }

  // Room Designation methods
  async getDowngradeProgress() {
    return this.handleRequest<RoomDowngradeProgressDto>(
      this.client.get("/api/admin/rooms/downgrade-progress"),
    );
  }

  async designateRoomsForArchive(request: DesignateRoomsRequest) {
    return this.handleRequest<DesignationResultDto>(
      this.client.post("/api/admin/rooms/designate-for-archive", request),
    );
  }

  async undesignateRooms(request: UndesignateRoomsRequest) {
    return this.handleRequest<DesignationResultDto>(
      this.client.post("/api/admin/rooms/undesignate", request),
    );
  }

  async canArchiveRoom(roomId: string) {
    return this.handleRequest<CanArchiveRoomResponse>(
      this.client.get(`/api/admin/rooms/${roomId}/can-archive`),
    );
  }
}
